import { IComponentOptions, IFormController } from "angular";
import { EventEmitter } from "../../../common/components/event-emitter";
import templateUrl from "./add-bank-account.component.html";
import { BankAccountCreationPolicy } from "../../../core/services";
import { DepositAmountType } from "../../roster/bank-accounts/deposit-amount-type";

/**
 * A component for filling in the form to add a bank account with
 * optional direct deposit information.
 */
export const AddBankAccountComponent: IComponentOptions = {
    templateUrl,
    require: {
        editForm: "^^?form"
    },
    bindings: {
        depositAmountTypes: "<",
        bankAccountCreationPolicy: "<",
        changeAccount: "&onChangeAccount"
    },
    controller: class AddBankAccountComponent {
        editForm?: IFormController;
        account: NewBankAccount = {};
        depositAmountTypes: DepositAmountType[];
        changeAccount: EventEmitter<{ account: ValidNewBankAccount }>;
        bankAccountCreationPolicy: BankAccountCreationPolicy;
        state = {
            view: 'DirectDeposit'
        };

        onChangeAccount() {
            if (this.editForm) {
                this.editForm.routingNumber.$setValidity("validRoutingNumber", this.isRoutingNumberValid(this.account))
                if (this.account.institutionNumber) {
                    this.editForm.institutionNumber.$setValidity("validInstitutionNumber", this.isInstitutionNumberValid(this.account))
                    this.editForm.confirmInstitutionNumber.$setValidity("matching", this.institutionNumberMatching(this.account))
                }
                this.editForm.confirmRoutingNumber.$setValidity("matching", this.routingNumberMatching(this.account))
                this.editForm.confirmAccountNumber.$setValidity("matching", this.accountNumberMatching(this.account))
                if (this.editForm.$invalid) {
                    return;
                }
            }
            this.changeAccount({
                $event: {
                    account: {
                        bankName: this.account.bankName,
                        accountNumber: this.account.accountNumber,
                        routingNumber: this.account.routingNumber,
                        institutionNumber: this.account.institutionNumber || null,
                        depositAmountTypeID: this.account.depositAmountTypeID || null,
                        depositAmount: this.account.depositAmount || null,
                        voidCheck: this.account.voidCheck || null,
                        adpDirectDeposit: this.account.adpDirectDeposit || null
                    }
                }
            })
        }

        isRoutingNumberValid(account: NewBankAccount): boolean {
            return account.routingNumber && account.routingNumber.length ===
                this.bankAccountCreationPolicy.routingNumberLength && /^\d+$/.test(account.routingNumber);
        }

        isInstitutionNumberValid(account: NewBankAccount): boolean {
            return account.institutionNumber && account.institutionNumber.length ===
                this.bankAccountCreationPolicy.institutionNumberLength && /^\d+$/.test(account.institutionNumber);
        }

        routingNumberMatching(account: NewBankAccount): boolean {
            return account.routingNumber && account.routingNumber === account.confirmRoutingNumber;
        }

        accountNumberMatching(account: NewBankAccount): boolean {
            return account.accountNumber && account.accountNumber === account.confirmAccountNumber;
        }

        institutionNumberMatching(account: NewBankAccount): boolean {
            return account.institutionNumber && account.institutionNumber === account.confirmInstitutionNumber;
        }

        revealSensitiveCharacter(text: string, i: number, char: string) {
            // Reveal the last 4 characters.
            return i > char.length - 4;
        }
    }
};

export interface ValidNewBankAccount {
    bankName: string;
    routingNumber: string;
    accountNumber: string;
    institutionNumber: string | null;
    depositAmountTypeID: number | null;
    depositAmount: number | null;
    voidCheck: File | null;
    adpDirectDeposit: File | null;
}

interface NewBankAccount {
    bankName?: string;
    routingNumber?: string;
    confirmRoutingNumber?: string;
    accountNumber?: string;
    confirmAccountNumber?: string;
    institutionNumber?: string;
    confirmInstitutionNumber?: string;
    depositAmountTypeID?: number;
    depositAmount?: number;
    voidCheck?: File;
    adpDirectDeposit?: File;
}
